import React, { FC } from 'react';
import { Alert } from 'reactstrap';
import { HotelReservation, PublicHotelReleaseGroup } from '../../../shared/hotel';
import { tzCorrectedDate } from '../../../shared/util/tzCorrectedDate';
import { RichMarkdownIt } from '../../components';
import { useTranslation } from '../../translations';

export const WaveEarlyEntryPeriodAlert: FC<{ readonly wave: PublicHotelReleaseGroup }> = ({
  wave,
}) => {
  const { ts } = useTranslation();

  return (
    <Alert color="warning" fade={false}>
      <p>
        <RichMarkdownIt
          source={ts(
            'this_wave_is_currently_open_for_early_entries_but',
            formatDate(wave.releaseStartAt),
          )}
        />
      </p>
      <p>
        {ts(
          'hotel.you_can_enter_the_queue_now_or_when_the_reservation_period_begins_but_links_will_be_issued_on_a_firstcome_firstserved_basis_starting_with_entries_from_the_early_entry_period',
        )}
      </p>
    </Alert>
  );
};

const formatDate = (
  date: Date,
  /*
   * Destructuring will not work as undefined is assumed to be not set by the user,
   * and overwriting the undefined with the default value.
   */
  // eslint-disable-next-line unicorn/no-object-as-default-parameter
  opts: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  },
  fixTimezoneOffset = false,
): string => {
  if (fixTimezoneOffset) {
    return formatDate(tzCorrectedDate(date), opts);
  }

  return date.toLocaleString(undefined, opts);
};

/**
 * @param date The date to calculate the time until
 *
 * @returns A string representing the Minutes, Hours, or Days until the date
 */
export const timeUntil = (date: Date): string => {
  const seconds = timeBetween(new Date(), date);

  if (seconds <= 60) {
    return 'less than a minute';
  }

  const minutes = Math.floor(seconds / 60);

  if (seconds <= 3600) {
    return `${minutes} minute${minutes === 1 ? '' : 's'}`;
  }

  const hours = Math.floor(minutes / 60);

  if (hours <= 24) {
    return `${hours} hour${hours === 1 ? '' : 's'}`;
  }

  const days = Math.floor(hours / 24);

  return `${days} day${days === 1 ? '' : 's'}`;
};

/**
 * Calculates the difference in seconds between two dates.
 * @param start The starting date.
 * @param end The ending date.
 * @returns The difference in seconds.
 */
const timeBetween = (start: Date, end: Date): number => {
  return Math.abs(end.getTime() - start.getTime()) / 1000;
};

interface ReservationStay {
  nights: number;
  stayDates: string;
}

interface GetReservationNightsProps {
  reservation: HotelReservation;
}

export const getReservationNights = ({
  reservation,
}: GetReservationNightsProps): ReservationStay | null => {
  const { checkIn: checkInStr, checkOut: checkOutStr } = reservation;

  if (!checkInStr || !checkOutStr) {
    return null;
  }

  const checkIn = new Date(checkInStr);
  const checkOut = new Date(checkOutStr);

  const isSameMonth = checkIn.getMonth() === checkOut.getMonth();
  const isSameYear = checkIn.getFullYear() === checkOut.getFullYear();

  const formattedCheckIn = formatDate(
    checkIn,
    {
      day: '2-digit',
      month: 'short',
      year: isSameYear ? undefined : 'numeric',
    },
    true,
  );

  const formattedCheckOut = formatDate(
    checkOut,
    {
      day: '2-digit',
      month: isSameMonth && isSameYear ? undefined : 'short',
    },
    true,
  );

  const checkOutYear = checkOut.getFullYear();

  const seconds = timeBetween(checkIn, checkOut);
  const nights = Math.floor(seconds / (60 * 60 * 24));

  return {
    nights,
    stayDates: `${formattedCheckIn} - ${formattedCheckOut}, ${checkOutYear}`,
  };
};
